/**
 * Wire types for the core service's release-note endpoints.
 *
 * Every text field carries both languages. That is not redundancy: switching the
 * UI language must re-render the release content without a new request, so the
 * client holds both translations and picks one at render time. An API shaped as
 * `?lang=de` would force a refetch on every language switch.
 */

/**
 * The four products a customer buys and opens. Deliberately no `admin` and no
 * `platform`: administration is an internal surface, and a "platform" bucket
 * would collect shared libraries, the shell and infrastructure, none of which a
 * reader recognises or can act on. Work in those repos is attributed to the
 * product whose behaviour it changed.
 */
export const MODULES = ["extraction", "mapping", "search", "dqm"] as const;

export type Module = (typeof MODULES)[number];

export const CHANGE_TYPES = ["new", "improved", "fixed"] as const;

export type ChangeType = (typeof CHANGE_TYPES)[number];

export type ReleaseStatus = "draft" | "published";

export interface LocalizedText {
  en: string;
  de: string;
}

export interface Change {
  type: ChangeType;
  text: LocalizedText;
}

export interface ReleaseNoteContent {
  title: LocalizedText;
  highlight: LocalizedText;
  changes: Change[];
}

export interface PublicReleaseNote extends ReleaseNoteContent {
  module: Module;
  version: string;
  /** ISO-8601. Ordering is always by this, never by the version string. */
  released_at: string;
}

export interface ReleaseNote extends PublicReleaseNote {
  status: ReleaseStatus;
  created_at: string;
  created_by: string;
  updated_at?: string | null;
  updated_by?: string | null;
  published_at?: string | null;
  published_by?: string | null;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface UnseenReleases {
  items: PublicReleaseNote[];
  /**
   * The value to echo back to `POST /seen`. Comes from the server precisely so a
   * client with a skewed clock cannot acknowledge past a release it never showed.
   */
  latest_released_at: string | null;
}

export interface ReleaseNoteCreate extends ReleaseNoteContent {
  module: Module;
  version: string;
  released_at: string;
}

export type ReleaseNoteUpdate = ReleaseNoteContent & {
  released_at?: string | null;
};

export const emptyLocalizedText = (): LocalizedText => ({ en: "", de: "" });

export const emptyContent = (): ReleaseNoteContent => ({
  title: emptyLocalizedText(),
  highlight: emptyLocalizedText(),
  changes: [],
});
